import { useEffect, useRef, useState } from 'react'
import { papanMinggu } from '../lib/minggu'
import { PRODUK_SIAP, type Produk } from '../lib/data'

/**
 * Papan minggu — inti halaman depan.
 *
 * Kolomnya adalah tujuh hari SUNGGUHAN mulai hari ini menurut jam lokal
 * pengunjung, jadi yang dilihat calon user di Jakarta berbeda tanggalnya dari
 * yang dilihat di Makassar. Isi slotnya contoh, dan itu ditulis terang-terangan
 * di keterangan bawah papan — yang tidak boleh dikarang adalah kemampuan
 * produk, bukan bentuk tampilannya.
 *
 * Produk yang belum dibangun tetap punya jalurnya sendiri, tapi selnya tidak
 * bisa diisi. Halaman ini menjual rencana, bukan janji.
 */

/** Contoh irama satu minggu untuk produk yang sudah bisa dipakai. */
const CONTOH_SLOT: Record<string, (string | null)[]> = {
  pinboss: ['07:30', null, '12:00', '19:00', '07:30', null, '10:00'],
}

const KOLOM = 'grid-cols-[8.5rem_repeat(7,minmax(4.5rem,1fr))]'

export function PapanMinggu({ produk }: { produk: Produk[] }) {
  const hari = papanMinggu()
  const [tampil, setTampil] = useState(false)
  const papan = useRef<HTMLDivElement>(null)

  // Satu gerakan yang disengaja: slot mengendap masuk saat papannya terlihat,
  // sekali saja. Tanpa IntersectionObserver, animasinya sudah selesai sebelum
  // papannya sampai ke layar.
  useEffect(() => {
    const node = papan.current
    if (!node) return

    const pengamat = new IntersectionObserver(
      ([entri]) => {
        if (entri.isIntersecting) {
          setTampil(true)
          pengamat.disconnect()
        }
      },
      { threshold: 0.25 },
    )
    pengamat.observe(node)
    return () => pengamat.disconnect()
  }, [])

  return (
    <div ref={papan}>
      {/* Papan: di layar sempit ia digeser mendatar. Tujuh kolom hari tidak bisa
          dipadatkan tanpa kehilangan artinya, dan memecahnya jadi dua baris
          membuat hubungan hari–produk hilang. */}
      <div className="overflow-x-auto">
        <div className={`grid ${KOLOM} min-w-[40rem] border-y border-border`}>
          <div className="flex items-end py-3 pr-4 text-xs text-muted-foreground">
            Starting today
          </div>
          {hari.map((h) => (
            <div
              key={h.key}
              className={`relative px-2 py-3 text-center ${
                h.hariIni ? 'text-foreground' : 'text-muted-foreground'
              }`}
            >
              {h.hariIni && <span aria-hidden className="absolute inset-x-2 top-0 h-px bg-primary" />}
              <div className="text-xs">{h.hari}</div>
              <div className="mt-0.5 text-sm tabular-nums">
                {h.tanggal} <span className="text-xs">{h.bulan}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Katalog belum sampai: satu jalur kosong supaya papannya tidak melompat. */}
        {produk.length === 0 && (
          <div className={`grid ${KOLOM} min-w-[40rem] border-b border-border`}>
            <div className="flex items-center py-4 pr-4 text-sm text-muted-foreground">
              Loading…
            </div>
            <div className="[grid-column:2/-1] min-h-[5.25rem] border-l border-border/60" />
          </div>
        )}

        {produk.map((p) => {
          const siap = PRODUK_SIAP.has(p.code)
          const slot = CONTOH_SLOT[p.code] ?? []

          return (
            <div key={p.code} className={`grid ${KOLOM} min-w-[40rem] border-b border-border`}>
              <div className="sticky left-0 z-10 flex flex-col justify-center gap-1 bg-background py-4 pr-4">
                <span className="text-sm font-medium">{p.name}</span>
                <span className="text-xs text-muted-foreground">
                  {siap ? p.description : 'Coming soon'}
                </span>
              </div>

              {siap ? (
                hari.map((h, i) => (
                  <div
                    key={h.key}
                    className={`min-h-[5.25rem] border-l border-border/60 p-1.5 ${
                      h.hariIni ? 'bg-primary/[0.05]' : ''
                    }`}
                  >
                    {slot[i] && (
                      <div
                        className={`flex items-center gap-1.5 rounded-sm border border-border bg-card px-1.5 py-1 motion-safe:transition-all motion-safe:duration-700 motion-safe:ease-out ${
                          tampil
                            ? 'translate-y-0 opacity-100 blur-none'
                            : 'motion-safe:translate-y-1 motion-safe:opacity-0 motion-safe:blur-[3px]'
                        }`}
                        style={{ transitionDelay: `${i * 60}ms` }}
                      >
                        <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-primary" />
                        <span className="text-[11px] tabular-nums">{slot[i]}</span>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="[grid-column:2/-1] flex min-h-[5.25rem] items-center border-l border-border/60 px-3">
                  <span className="papan-kosong flex w-full items-center rounded-sm px-3 py-2 text-xs text-muted-foreground">
                    Can't be filled from here yet
                  </span>
                </div>
              )}
            </div>
          )
        })}
    </div>

        <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-muted-foreground">
          <span className="flex items-center gap-2">
            <span aria-hidden className="size-1.5 rounded-full bg-primary" />
            Scheduled, waiting its turn
          </span>
          <span className="flex items-center gap-2">
            <span aria-hidden className="papan-kosong h-3 w-6 rounded-[2px]" />
            Product not built yet
          </span>
          <span>The slots above are examples, not your schedule.</span>
          <span className="md:hidden">Scroll the board to see the next days.</span>
        </div>
    </div>
  )
}